import { Component, ErrorInfo, ReactNode } from 'react';
import Button from '@/shared/ui/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info);
  }

  handleReset = () => this.setState({ hasError: false, error: undefined });

  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message ?? '';
      const isStaleChunk =
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed');

      return (
        this.props.fallback ?? (
          <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
            <h1 className="text-2xl font-bold text-red-600">Something went wrong</h1>
            <p className="max-w-md text-gray-600">
              {isStaleChunk
                ? 'The app could not load a page module. This usually clears after a full refresh, especially during local development.'
                : message}
            </p>
            <Button
              type="button"
              variant="marketing"
              size="lg"
              onClick={() => (isStaleChunk ? window.location.reload() : this.handleReset())}
            >
              {isStaleChunk ? 'Reload page' : 'Try again'}
            </Button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
